import { Condition, FormField } from './types';

export function matchesCondition(condition: Condition | undefined, values: Record<string, unknown>, fieldVisibility?: Record<string, boolean>): boolean {
  if (!condition) return true;
  if (condition.all) return condition.all.length > 0 && condition.all.every(c => matchesCondition(c, values, fieldVisibility));
  if (condition.any) return condition.any.some(c => matchesCondition(c, values, fieldVisibility));
  if (condition.none) return condition.none.every(c => !matchesCondition(c, values, fieldVisibility));
  if (condition.one) return condition.one.filter(c => matchesCondition(c, values, fieldVisibility)).length === 1;
  if (condition.not) return condition.not.length === 1 && !matchesCondition(condition.not[0], values, fieldVisibility);
  const key = condition.field || '';
  const current = values[key];
  const expected = condition.value;
  switch (condition.operator) {
    case 'present': return Object.prototype.hasOwnProperty.call(values, key);
    case 'visible': return !!key && !!fieldVisibility?.[key];
    case 'hidden': return !!key && fieldVisibility?.[key] === false;
    // 'changed' is a reset-rule trigger; it is resolved against the previous value by FormRenderer.
    case 'changed': return false;
    case 'equals': return current === expected;
    case 'not_equals': return current !== expected;
    case 'in': return Array.isArray(expected) && expected.includes(current);
    case 'not_in': return Array.isArray(expected) && !expected.includes(current);
    case 'contains': return Array.isArray(current) ? current.includes(expected) : String(current ?? '').includes(String(expected ?? ''));
    case 'not_contains': return !matchesCondition({ ...condition, operator: 'contains' }, values, fieldVisibility);
    case 'empty': return current == null || current === '' || (Array.isArray(current) && current.length === 0);
    case 'not_empty': return !matchesCondition({ ...condition, operator: 'empty' }, values, fieldVisibility);
    case 'gt': return Number(current) > Number(expected);
    case 'gte': return Number(current) >= Number(expected);
    case 'lt': return Number(current) < Number(expected);
    case 'lte': return Number(current) <= Number(expected);
    default: return false;
  }
}


export function resolveFieldVisibility(fields: FormField[], values: Record<string, unknown>): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  fields.forEach((field) => { visibility[field.key] = true; });

  // Resolve visibility to a fixed point so a condition can inspect whether
  // another dynamically controlled field is currently visible/hidden.
  for (let pass = 0; pass <= fields.length; pass += 1) {
    let changed = false;
    fields.forEach((field) => {
      const next = matchesCondition(field.visible_when, values, visibility);
      if (visibility[field.key] !== next) {
        visibility[field.key] = next;
        changed = true;
      }
    });
    if (!changed) break;
  }
  return visibility;
}
